import { InformationIcon } from "@/assets/icons";
import Divider from "@/components/ui/Divider";
import Dropdown from "@/components/ui/dropdown";
import { IOptionItem } from "@/components/ui/dropdown/types";
import Tooltip from "@/components/ui/tooltip";
import { MESSAGE } from "@/constants/message";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { SpatialAggregationType } from "@/types/common";
import { GridConfigAggregationType, H3Configuration } from "@/types/dataset";
import { EOutputAnalysisType } from "@/types/spatial";

type AggregationFilterProps = {
  spatialType?: SpatialAggregationType;
  value?: H3Configuration | null;
  dataSource?: string;
  onChangeAggregationType: (value: GridConfigAggregationType) => void;
  onChangeGroupBy: (value: string) => void;
  openFrom: EAggregationFilterOpenFrom;
  groupByAttributeOptions: IOptionItem[];
  groupByValue?: string;
};

type EAggregationFilterOpenFrom = "datasetModal" | "editPopup";

const AGGREGATION_OPTIONS = [
  {
    value: "AVG",
    label: "AVG"
  },
  {
    value: "SUM",
    label: "SUM"
  }
];

type AggregationValue = (typeof AGGREGATION_OPTIONS)[number]["value"];

const DESCRIPTION_AGGREGATION_INFO: Record<AggregationValue, string> = {
  AVG: "Average number of unique devices calculated per interval in the selected period.",
  SUM: "Total device count calculated from all time intervals. A device may be counted more than once if it appears in multiple intervals."
};

const AggregationFilter = ({
  spatialType,
  value,
  dataSource = "bvt",
  onChangeAggregationType,
  onChangeGroupBy,
  openFrom,
  groupByAttributeOptions,
  groupByValue
}: AggregationFilterProps) => {
  const { isGroupByFilterH3DatasetEnabled } = useAppFeatures();

  const handleChangeAggregationType = (option: IOptionItem | null) => {
    if (!option) return;
    // Safe: AGGREGATION_OPTIONS is the sole data source and only contains GridConfigAggregationType values.
    onChangeAggregationType(option.value as GridConfigAggregationType);
  };

  const handleChangeGroupBy = (option: IOptionItem | null) => {
    if (!option) return;
    onChangeGroupBy(option.value);
  };

  if (spatialType !== EOutputAnalysisType.H3 || dataSource !== "bvt")
    return null;

  const aggregation =
    AGGREGATION_OPTIONS.find((item) => item.value === value?.aggregationType) ??
    AGGREGATION_OPTIONS[0]; // Default to AVG if not found

  const groupByOptions = [
    { value: "none", label: "None" },
    ...groupByAttributeOptions
  ];

  const groupBy =
    groupByOptions.find((item) => item.value === groupByValue) ??
    groupByOptions[0]; // Default to None if not found

  return (
    <>
      <Divider className="my-3" />
      <div className="flex flex-col gap-3">
        {isGroupByFilterH3DatasetEnabled && (
          <>
            <div className="flex gap-2 items-center">
              <label
                htmlFor="groupBy"
                className="text-sm font-semibold text-txtBody"
              >
                Group By
              </label>
            </div>
            <Dropdown
              name="groupBy"
              showLabelOnTop={false}
              placeholder="Select group"
              data={groupByOptions}
              value={groupBy}
              onSelect={handleChangeGroupBy}
            />
          </>
        )}
        <div className="flex gap-2 items-center">
          <label
            htmlFor="aggregation"
            className="text-sm font-semibold text-txtBody"
          >
            Aggregation
          </label>
          {!isGroupByFilterH3DatasetEnabled && openFrom === "datasetModal" && (
            <Tooltip
              content={MESSAGE.AGGREGATION_INFO_TOOLTIP}
              className="max-w-[190px]"
              position="top-right"
              arrowPadding={16}
            >
              <button
                type="button"
                aria-label="Aggregation Info"
                className="bg-transparent border-0 p-0"
              >
                <InformationIcon title="Aggregation Info" />
              </button>
            </Tooltip>
          )}
          {isGroupByFilterH3DatasetEnabled && openFrom === "datasetModal" && (
            <Tooltip
              content={
                <div className="space-y-2">
                  <p className="font-semibold text-[10px]">
                    AVG:{" "}
                    <span className="font-light">
                      {DESCRIPTION_AGGREGATION_INFO.AVG}
                    </span>
                  </p>
                  <p className="font-semibold text-[10px]">
                    SUM:{" "}
                    <span className="font-light">
                      {DESCRIPTION_AGGREGATION_INFO.SUM}
                    </span>
                  </p>
                </div>
              }
              className="max-w-[190px]"
              position="top-right"
              arrowPadding={16}
            >
              <button
                type="button"
                aria-label="Aggregation Info"
                className="bg-transparent border-0 p-0"
              >
                <InformationIcon title="Aggregation Info" />
              </button>
            </Tooltip>
          )}
        </div>
        <div className="space-y-1">
          <Dropdown
            name="aggregation"
            showLabelOnTop={false}
            placeholder="Aggregation"
            data={
              isGroupByFilterH3DatasetEnabled
                ? AGGREGATION_OPTIONS
                : AGGREGATION_OPTIONS.slice(0, 1)
            }
            value={aggregation}
            onSelect={handleChangeAggregationType}
          />
          {isGroupByFilterH3DatasetEnabled && (
            <p className="text-[10px] text-txtBody">
              The aggregation of amounts can lead to inaccurate results if there
              is data duplication.
            </p>
          )}
        </div>
      </div>
    </>
  );
};

export default AggregationFilter;
